<template>
  <cs-preloader-overlay :loading="loading">
    <div id="warp" class="wide">
      <div class="wrap-container">
        <div :class="$style.header">
          <project-header
            :project="project"
            :language="getLanguage"
          />
        </div>

        <payments-disabled
          v-if="!project.paymentsEnabled || !project.validated"
        />

        <div class="container wrap-box">
          <payment-description
            :description="description"
            :tilda-parameters="tildaData"
          />

          <div v-if="isShownFieldByName('rating')" v-m-b="10">
            <grade-management
              ref="rating"
              v-model="rating"
              :rating="project.senderFields.rating"
            />
          </div>

          <div v-if="tips.length > 0" v-m-b="10">
            <tips-management
              :bill-amount="amountFormURL"
              :currency="project.currency"
              :tips-amount="formattedAmount"
              :tips-list="tips"
              @change="handleChangeTipsAmount"
            />
          </div>

          <form>
            <div v-if="senderKeysList.length || project.safeDeal" class="special-fields">
              <InputText
                v-if="isShownFieldByName('name')"
                ref="name"
                v-model="formData.name"
                class="focusElements"
                inputId="name"
                iconClass="icon-user"
                :inputTitle="$t('enter.name')"
                :input-placeholder="$t('enter.name')"
                :maxLength="50"
                :readonly="isFieldReadOnly('name')"
              />

              <InputEmail
                v-if="isShownFieldByName('email')"
                ref="email"
                v-model="formData.email"
                class="focusElements"
                inputId="email"
                iconClass="icon-mail"
                :inputTitle="$t('enter.email')"
                :input-placeholder="$t('enter.email')"
                :readonly="isFieldReadOnly('email')"
                required
              />

              <InputPhone
                v-if="isShownFieldByName('phone')"
                ref="phone"
                v-model="formData.phone"
                class="focusElements"
                inputId="phone"
                iconClass="icon-phone"
                :inputTitle="$t('enter.phone')"
                :input-placeholder="$t('enter.phone')"
                :readonly="isFieldReadOnly('phone')"
                required
              />

              <InputTextarea
                v-if="isShownFieldByName('comment')"
                ref="comment"
                v-model="formData.comment"
                class="focusElements"
                inputId="comment"
                iconClass="icon-message"
                :inputTitle="$t('enter.comment')"
                :input-placeholder="$t('enter.comment')"
                :readonly="isFieldReadOnly('comment')"
              />

              <InputTextarea
                v-if="isShownFieldByName('message')"
                ref="message"
                v-model="formData.message"
                class="focusElements"
                inputId="message"
                :inputName="$t('enter.message')"
                iconClass="icon-receipt"
                :inputTitle="$t('enter.message')"
                :input-placeholder="$t('enter.message')"
                :readonly="isFieldReadOnly('message')"
              />

              <InputTextarea
                v-if="isShownFieldByName('address')"
                ref="address"
                v-model="formData.address"
                class="focusElements"
                input-id="address"
                icon-class="icon-map-pin"
                :input-title="$t('enter.address')"
                :input-placeholder="$t('enter.address')"
                :readonly="isFieldReadOnly('address')"
              />
            </div>

            <div
              class="payment-amount"
              :class="{
                'single-block': isSingleBlockView
              }"
            >
              <InputPrice
                ref="amount"
                v-model.lazy="amount"
                class="focusElements"
                input-id="amount"
                :input-name="$t('enter.sum.for.pay')"
                :input-title="$t('enter.transaction.sum')"
                :commission="commission"
                :min-value="project.min_amount"
                :max-value="project.max_amount"
                :readonly="isReadonlyAmount"
                :currency="project.currency"
                required
                @change="amount = $event"
              />

              <div v-if="isShownComissionInfo" class="payment-comission">
                <span v-if="commission" v-text="displayComission"></span>
                <span>{{ commission ? `${this.$t('comission.of.service')}` : `${this.$t('comission.none')}` }}</span>
              </div>

              <div v-if="isShownPaymentButtons" class="payment-buttons">
                <button
                  v-if="isCardPaymentAvailable"
                  data-test="personalPaymentCardPaymentButton"
                  :class="{
                    'payment-button': true,
                    'loading': loading,
                    'disabled': !isValid,
                  }"
                  @click.prevent="goPay($event, null)"
                  v-text="displayAmount"
                ></button>

                <div v-if="isShownYandexPayment" class="yandex-payment-wrapper payment-button">
                  <YandexPay
                    data-test="billPaymentYandexPayButton"
                    :merchantName="project.project_name"
                    :amount="preparedAmount"
                    :disabled="!isValid"
                    :requestData="getPaymentData"
                    @loading="handleLoading"
                    @click-on-disabled="validateForm"
                  />
                </div>

                <div v-if="isApplePayAvailable" class="apple-payment-wrapper payment-button">
                  <ApplePay
                    data-test="personalPaymentApplePayButton"
                    :label="project.project_name"
                    :amount="preparedAmount"
                    :disabled="!isValid"
                    :requestData="getPaymentData"
                    @loading="handleLoading"
                    @click-on-disabled="validateForm"
                  />
                </div>

                <div v-if="isGooglePayAvailable" class="google-payment-wrapper payment-button">
                  <GooglePay
                    data-test="personalPaymentGooglePayButton"
                    :label="project.project_name"
                    :amount="preparedAmount"
                    :disabled="!isValid"
                    :requestData="getPaymentData"
                    @loading="handleLoading"
                    @click-on-disabled="validateForm"
                  />
                </div>

                <button
                  v-if="isQiwiPaymentAvailable"
                  data-test="personalPaymentQiwiButton"
                  class="qiwi-payment-button payment-button"
                  :class="{
                    'loading': loading,
                    'disabled': !isValid,
                  }"
                  @click.prevent="goPay($event, 'QIWI')"
                >
                  <img src="~@/assets/svg/qiwi.svg" alt="">
                  <span>Qiwi</span>
                </button>

                <button
                  v-if="isYooMoneyPaymentAvailable"
                  data-test="personalPaymentYoomoneyButton"
                  class="yoomoney-payment-button payment-button"
                  :class="{
                    'loading': loading,
                    'disabled': !isValid,
                  }"
                  @click.prevent="goPay($event, 'YOOMONEY')"
                >
                  <img src="~@/assets/svg/yoomoney.svg" alt="">
                  <span>{{ $t('common.yoomoney') }}</span>
                </button>

                <template v-if="hasYooMoneyWidget">
                  <div id="payment-form" ref="widgetYooMoney" style="padding: 0;"></div>
                </template>

                <button
                  v-if="isPayMePaymentAvailable"
                  data-test="personalPaymentPayMeButton"
                  class="payme-payment-button payment-button"
                  :class="{
                    'loading': loading,
                    'disabled': !isValid,
                  }"
                  @click.prevent="goPay($event, 'PAYME')"
                >
                  <img src="~@/assets/svg/payme.svg" alt="">
                </button>
              </div>
            </div>

            <div class="policy-privacy" v-html="displayPolicyPrivacy"></div>

            <div v-if="project.selfEmployed" class="self-employed-wrapper">
              <PaymentSelfEmployed />
            </div>
          </form>

          <FooterSection />
        </div>
      </div>

      <unsafe-deal-info-dialog
        v-if="project.safeDeal"
        v-model="isShownUnsafeDealInfo"
        @submit="handleSubmitUnsafeDeal"
      />
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { isSafari, isAndroid } from 'mobile-device-detect';

import throttle from 'lib/utils/throttle';

import { formatAmount } from 'lib/utils/amount';
import { calcPercent } from 'lib/utils/number';

// import logrocket from '@/services/logrocket';
import HTTPService from '@/services/Http';

import { parseURL, replaceURL } from '../helper';

import PaymentDescription from '@/components/pages/PaymentDescription';
import PaymentSelfEmployed from '@/components/pages/PaymentSelfEmployed';
import PaymentsDisabled from '@/components/pages/PaymentsDisabled';

import ApplePay from '@/modules/payments/ApplePay';
import GooglePay from '@/modules/payments/GooglePay';
import YandexPay from '@/modules/payments/YandexPay';

import ProjectHeader from 'organisms/project/ProjectHeader';
import UnsafeDealInfoDialog from 'organisms/payment/components/UnsafeDealInfoDialog';
import GradeManagement from 'organisms/grade/GradeManagement';
import TipsManagement from 'organisms/tips/TipsManagement';

import { MERCHANT_ID } from '@/.env';

const PAYMENT_PARAMETERS = ['amount', 'tipPercents'];
const INFORMATION_PARAMETERS = ['description'];
const REQUIRED_PARAMETERS = ['phone', 'email'];
const SINGLES_PARAMETERS = ['comment', 'message', 'address'];
const SENDER_PARAMETERS = ['name', ...REQUIRED_PARAMETERS, ...SINGLES_PARAMETERS];
const TECHNIC_PARAMETERS = ['readonly', 'istest'];
const TILDA_PARAMETERS = ['action', 'orderId'];
const FULL_PARAMETERS = [
  ...PAYMENT_PARAMETERS,
  ...INFORMATION_PARAMETERS,
  ...SENDER_PARAMETERS,
  ...TECHNIC_PARAMETERS,
  ...TILDA_PARAMETERS,
];
const NON_VALIDATED_FIELDS = ['rating'];

const UNSAFE_PAYMENT_TYPES = ['QIWI', 'YOOMONEY', 'PAYME'];

export default {
  name: 'PaymentAmount',
  components: {
    PaymentDescription,
    PaymentSelfEmployed,
    PaymentsDisabled,
    ApplePay,
    GooglePay,
    YandexPay,
    ProjectHeader,
    UnsafeDealInfoDialog,
    GradeManagement,
    TipsManagement,
  },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  created() {
    console.log('NODE_ENV', process.env.NODE_ENV);

    this.parsePaymentURL();
    this.setPaymantData();
    this.setDisabledFields();
    this.getTildaInfo();

    this.initializeApplePayment();
    this.initializeGooglePayment();
    this.initializeYandexPayment();

    this.setFocusToAvailableInputField();
    this.displayAmountByPaymentDisabled();

    if (this.$validate.isObjectWithFields(this.$route.query, ['__YP__'])) {
      this.loading = true;
    }
  },
  data() {
    return {
      loading: true,
      interval: null,

      amount: '',
      commission: 0,
      commissionLoading: false,
      paymentId: '',
      isTest: false,

      tildaData: {},
      formData: {
        name: '',
        phone: '',
        email: '',
        comment: '',
        message: '',
        address: '',
      },

      isApplePayAvailable: false,
      isGooglePayAvailable: false,
      isYandexPayAvailable: false,

      parsedURLParameters: {},
      readonlyURLParameters: [],

      checkout: null,
      isShownUnsafeDealInfo: false,
      paymentTypeByUnsafeDeal: null,
      rating: 0,
      tips: [],
      amountFormURL: null,
    };
  },
  computed: {
    significantZeros() {
      const { currency } = this.project;

      return currency.significant_zeros;
    },
    preparedAmount() {
      return this.amount.replace(/\s/g, '');
    },
    formattedAmount() {
      return Math.round(this.preparedAmount * 10 ** this.significantZeros);
    },
    displayAmount() {
      const amount = formatAmount(this.formattedAmount + this.commission, this.project.currency);

      return `${this.$t('common.transfer')} ${amount}`;
    },
    displayComission() {
      return `+ ${formatAmount(this.commission, this.project.currency)}`;
    },
    displayPolicyPrivacy() {
      return this.$t('policy.privacy.payment', {
        offerta: this.getOfferta,
        policy: this.getPolicyPrivacy,
        agreement: this.getAgreement,
      });
    },
    description() {
      const description = this.$validate.isObjectWithFields(this.parsedURLParameters, ['description'])
        ? this.parsedURLParameters.description
        : '';

      return description;
    },
    priceIsValid() {
      return !this.commissionLoading && this.$refs.amount && !this.$refs.amount.$v.$invalid;
    },
    senderKeysAreValid() {
      const keysList = this.senderKeysList;

      if (this.$validate.isEmptyArray(keysList)) {
        return false;
      }

      return keysList.every(fieldKey => {
        if (NON_VALIDATED_FIELDS.includes(fieldKey)) {
          return true;
        }

        if (REQUIRED_PARAMETERS.some(parameter => parameter === fieldKey)) {
          return this.formData[fieldKey].length && this.$refs[fieldKey].$v && !this.$refs[fieldKey].$v.$invalid;
        } else {
          return this.$refs[fieldKey] && this.$refs[fieldKey].$v && !this.$refs[fieldKey].$v.$invalid;
        }
      });
    },
    isValid() {
      if (!this.project.testPayment && !this.project.paymentsEnabled) {
        return false;
      }

      if (this.$validate.isNotEmptyArray(this.senderKeysList)) {
        return this.priceIsValid && this.senderKeysAreValid;
      }

      return this.priceIsValid;
    },
    senderKeysList() {
      const urlParameters = this.parsedURLParameters;

      const keysUrl = this.$validate.isNotEmptyObject(urlParameters)
        ? Object.keys(urlParameters).filter(value => SENDER_PARAMETERS.includes(value))
        : [];

      if (this.project.safeDeal && !keysUrl.includes('email')) {
        keysUrl.push('email');
      }

      const { senderFields } = this.project;

      if (this.$validate.isUndefined(senderFields)) {
        return [...keysUrl];
      }

      let keysApi = Object.keys(senderFields)
        .filter(value => senderFields[value].selected)
        .filter(value => !keysUrl.includes(value));

      if (this.hasSingleParametersInUrl) {
        keysApi = keysApi.filter(value => !SINGLES_PARAMETERS.includes(value));
      }

      return [...keysUrl, ...keysApi];
    },
    isSingleBlockView() {
      const senderKeysListWithoutRating = this.senderKeysList.filter(field => field !== 'rating');

      return this.$validate.isEmptyArray(senderKeysListWithoutRating);
    },
    senderDataMap() {
      const fields = this.formData;
      const fieldsKeys = Object.keys(fields);

      return fieldsKeys
        .filter(key => this.$validate.isNotEmptyString(fields[key]))
        .reduce((acc, curr) => {
          acc[curr] = fields[curr];
          return acc;
        }, {});
    },
    getPaymentData() {
      let requestData = {};

      requestData = {
        amount: {
          amount: this.formattedAmount,
          commission: this.commission,
          currency: this.project.currency.code,
        },
        projectId: this.project.project_id,
        sender: this.senderDataMap,
        isTest: Number(this.isTest),
        localization: this.getLanguage,
        currency: this.project.currency,
        rating: this.getRating,
        tips: this.getTips,
      };

      if (this.$route.query.orderId) {
        requestData.tildaOrderId = this.$route.query.orderId;
      }

      if (this.$validate.isObjectWithFields(this.parsedURLParameters, ['description'])) {
        requestData.description = this.description;
      }

      if (this.$route.query.widgetId) {
        requestData.widgetId = this.$route.query.widgetId;
      }

      return requestData;
    },
    isReadonlyAmount() {
      if (!this.project.paymentsEnabled) {
        return true;
      }

      const isTildaAmount = this.$validate.isNotEmptyObject(this.tildaData) && this.$validate.isNotNull(this.tildaData.amount);

      return this.project.amount_type !== 'dynamic' || this.isFieldReadOnly('amount') || isTildaAmount;
    },
    hasSingleParametersInUrl() {
      return Object.keys(this.parsedURLParameters)
        .some(parameter => SINGLES_PARAMETERS.includes(parameter));
    },
    isCardPaymentAvailable() {
      if (!this.project.paymentsEnabled) {
        return false;
      }

      return this.isCardPaymentEnabled;
    },
    isQiwiPaymentAvailable() {
      if (!this.project.paymentsEnabled) {
        return false;
      }

      if (this.isCurrencyCrypto) {
        return false;
      }

      return this.isQiwiPaymentEnabled;
    },
    isYooMoneyPaymentAvailable() {
      if (!this.project.paymentsEnabled) {
        return false;
      }

      if (this.isCurrencyCrypto) {
        return false;
      }

      return this.isYooMoneyPaymentEnabled;
    },
    isPayMePaymentAvailable() {
      if (!this.project.paymentsEnabled) {
        return false;
      }

      if (this.isCurrencyCrypto) {
        return false;
      }

      return this.isPayMePaymentEnabled;
    },
    isShownYandexPayment() {
      if (!this.project.paymentsEnabled) {
        return false;
      }

      if (!this.isYandexPayEnabled) {
        return false;
      }

      if (this.isCurrencyCrypto) {
        return false;
      }

      return this.isYandexPayAvailable;
    },
    getPaymentMethods() {
      return [
        this.isCardPaymentEnabled && 'bank_card',
        this.isGooglePayEnabled && 'google_pay',
        this.isApplePayEnabled && 'apple_pay',
      ].filter(i => i);
    },
    hasYooMoneyWidget() {
      if (!this.isExternalCheckoutProvider) {
        return false;
      }

      if (this.$validate.isEmptyArray(this.getPaymentMethods)) {
        return false;
      }

      return true;
    },
    isShownPaymentButtons() {
      return this.isCardPaymentAvailable
        || this.isShownYandexPayment
        || this.isApplePayAvailable
        || this.isGooglePayAvailable
        || this.isQiwiPaymentAvailable
        || this.isYooMoneyPaymentAvailable
        || this.hasYooMoneyWidget
        || this.isPayMePaymentAvailable;
    },
    getRating() {
      if (!this.rating) {
        return null;
      }

      return {
        rating: this.rating,
        type: this.project.senderFields.rating.type,
      };
    },
    getTips() {
      if (this.$validate.isEmptyArray(this.tips)) {
        return null;
      }

      if (this.$validate.isNull(this.amountFormURL)) {
        return null;
      }

      return {
        amount: this.amountFormURL,
        percent: calcPercent(this.formattedAmount, this.amountFormURL),
      };
    },
    isShownComissionInfo() {
      if (this.$validate.isFixedLengthArray(this.getPaymentEnabledOptions, 1)) {
        return this.getPaymentEnabledOptions[0].name !== 'QIWI';
      }

      return true;
    },
    ...mapGetters([
      'isApplePayEnabled',
      'isGooglePayEnabled',
      'isYandexPayEnabled',
      'isCardPaymentEnabled',
      'isQiwiPaymentEnabled',
      'isYooMoneyPaymentEnabled',
      'isPayMePaymentEnabled',
      'isExternalCheckoutProvider',
      'isExternalRedirect',
      'getOfferta',
      'getPolicyPrivacy',
      'getAgreement',
      'getLanguage',
      'isCurrencyCrypto',
      'getPaymentEnabledOptions',
    ]),
  },
  watch: {
    amount: {
      immediate: false,
      // eslint-disable-next-line prefer-arrow-callback
      handler: throttle(function(val) {
        this.processingExternalCheckout(val);

        this.loadCommission();
      }, 100),
    },
    formData: {
      deep: true,
      immediate: false,
      // eslint-disable-next-line prefer-arrow-callback
      handler: throttle(function(val) {
        if (this.isValid) {
          this.processingExternalCheckout(this.amount);
          return;
        }

        this.externalCheckoutClear();
      }, 500),
    },
  },
  methods: {
    async processingExternalCheckout(amount) {
      if (!this.hasYooMoneyWidget || !this.isValid) {
        this.externalCheckoutClear();
        return;
      }

      if (this.$validate.isEmptyString(amount)) {
        this.externalCheckoutClear();
        return;
      }

      const payload = this.getPaymentCreatePayload();

      try {
        const { data } = await this.PROJECT_PAYMENT_CREATE(payload);
        const { providerConfirmationToken, providerReturnUrl } = data;

        if (providerConfirmationToken && providerReturnUrl) {
          this.externalCheckoutInitialize(providerConfirmationToken, providerReturnUrl);
        }
      } catch (error) {
        console.log(error);
        this.externalCheckoutClear();
      }
    },
    getPaymentCreatePayload(config = {}) {
      const data = Object.assign({}, config, this.getPaymentData);

      return data;
    },
    async setFocusToAvailableInputField() {
      const focusElements = document.getElementsByClassName('focusElements');

      await this.$nextTick();
      for (const inputWrapper of focusElements) {
        const inputField = inputWrapper.querySelectorAll('textarea, input')[0];

        if (!inputField.readOnly) {
          return inputField.focus();
        }
      }
    },
    isShownFieldByName(value = null) {
      if (this.$validate.isNull(value)) {
        return false;
      }

      if (this.$validate.isObjectWithFields(this.parsedURLParameters, [value])) {
        return true;
      }

      if (value === 'email' && this.project.safeDeal) {
        return true;
      }

      if (this.$validate.isObjectWithFields(this.project.senderFields, [value])) {
        if (SINGLES_PARAMETERS.includes(value) && this.hasSingleParametersInUrl) {
          return false;
        }
        return this.project.senderFields[value].selected;
      }

      return false;
    },
    isFieldReadOnly(value = null) {
      if (this.$validate.isNull(value)) {
        return false;
      }
      return this.readonlyURLParameters.includes(value);
    },
    initializeApplePayment() {
      if (!this.project.paymentsEnabled) {
        return;
      }

      if (!this.isApplePayEnabled) {
        return;
      }

      this.SET_APPLE_PAY_SESSION(null);

      // eslint-disable-next-line no-undef
      this.isApplePayAvailable = window.ApplePaySession && ApplePaySession.canMakePayments();
    },
    initializeGooglePayment() {
      if (isSafari) {
        return;
      }

      if (!this.project.paymentsEnabled) {
        return;
      }

      if (!this.isGooglePayEnabled) {
        return;
      }

      try {
        if (isAndroid) {
          // eslint-disable-next-line no-undef
          this.isGooglePayAvailable = this.$validate.isNotUndefined(google) && 'PaymentRequest' in window;
        } else {
          // eslint-disable-next-line no-undef
          this.isGooglePayAvailable = this.$validate.isNotUndefined(google);
        }
      } catch (error) {
        // logrocket.logger(error);
      }
    },
    initializeYandexPayment() {
      // eslint-disable-next-line no-undef
      console.log('initialize.yandex.payment', YaPay, MERCHANT_ID);

      // eslint-disable-next-line no-undef
      this.isYandexPayAvailable = this.$validate.isNotUndefined(YaPay);
      console.log('YANDEX_PAY:isAvailable', this.isYandexPayAvailable);
    },
    parsePaymentURL() {
      const parsedData = parseURL(FULL_PARAMETERS, [...PAYMENT_PARAMETERS, ...SENDER_PARAMETERS]);
      replaceURL(parsedData);

      this.parsedURLParameters = parsedData;
    },
    setPaymantData() {
      const {
        name,
        email,
        phone,
        comment,
        message,
        address,
        amount,
        istest,
        tipPercents,
      } = this.parsedURLParameters;

      if (this.$validate.isNotUndefined(tipPercents)) {
        this.amountFormURL = Number(amount);

        this.tips = tipPercents.split(',')
          .map((percent, idx) => {
            return {
              id: idx,
              amount: Math.round(Number(amount) * Number(percent) / 100),
              percent: Number(percent),
            };
          });
      } else if (this.$validate.isNotUndefined(amount)) {
        this.amount = formatAmount(+amount, {
          significant_zeros: this.project.currency.significant_zeros,
          type: this.project.currency.type,
        });
      } else if (this.project.amount) {
        this.amount = formatAmount(this.project.amount, {
          significant_zeros: this.project.currency.significant_zeros,
          type: this.project.currency.type,
        });
      }
      if (this.$validate.isNotUndefined(name)) {
        this.formData.name = name;
      }
      if (this.$validate.isNotUndefined(email)) {
        this.formData.email = email;
      }
      if (this.$validate.isNotUndefined(phone)) {
        this.formData.phone = phone;
      }
      if (this.$validate.isNotUndefined(comment)) {
        this.formData.comment = comment;
      }
      if (this.$validate.isNotUndefined(message)) {
        this.formData.message = message;
      }
      if (this.$validate.isNotUndefined(address)) {
        this.formData.address = address;
      }
      if (this.$validate.isNotUndefined(istest)) {
        this.isTest = istest;
      }
    },
    setDisabledFields() {
      const { readonly } = this.parsedURLParameters;

      if (this.$validate.isUndefined(readonly) || this.$validate.isEmptyString(readonly)) {
        return;
      }

      this.readonlyURLParameters = readonly.split(',');
    },
    handleSubmitUnsafeDeal() {
      this.createPayment(this.paymentTypeByUnsafeDeal);
    },
    goPay(event, paymentType = null) {
      if (!this.isValid) {
        this.validateForm();
        return false;
      }

      if (this.useSafeDeal(paymentType)) {
        this.paymentTypeByUnsafeDeal = paymentType;
        this.isShownUnsafeDealInfo = true;
        return;
      }

      this.createPayment(paymentType);
    },
    createPayment(paymentType) {
      this.loading = true;
      this.SET_PAYMENT_TYPE(paymentType);

      const payload = Object.assign({}, this.getPaymentData);

      if (this.$validate.isNotNull(paymentType)) {
        payload.type = paymentType;
      }

      if (this.isCurrencyCrypto) {
        HTTPService.createCryptoPayment(payload)
          .then(response => {
            const { data } = response;
            console.log('create:crypto.payment', data);

            this.SET_TRANSACTION(data);
            this.SET_TRANSACTION_KEY(this.$route.params.id);

            this.$router.push({
              name: 'crypto.processing',
              query: {
                paymentId: data.id,
                processToken: data.processToken,
              },
            });
          })
          .catch(() => {
            this.$router.push({ name: 'service.error' });
          })
          .finally(() => {
            this.loading = false;
          });
        return;
      }

      HTTPService.createPayment(payload)
        .then(response => {
          const { data } = response;
          console.log('create:payment', data);

          this.SET_TRANSACTION(data);
          this.SET_TRANSACTION_KEY(this.$route.params.id);

          if (this.isExternalCheckoutProvider && this.isExternalRedirect && data.externalPayUrl) {
            window.location.assign(data.externalPayUrl);
            return;
          }

          if (data.externalPayUrl) {
            window.location.assign(data.externalPayUrl);
            return;
          }

          this.$router.push({ name: 'EnterCard' });
        })
        .catch(() => {
          this.$router.push({ name: 'service.error' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadCommission() {
      this.commissionLoading = true;

      if (isNaN(this.formattedAmount)) {
        return;
      }

      HTTPService.getCommission(this.$route.params.id, this.formattedAmount)
        .then(response => {
          this.commission = Number(response.data);
          this.SET_COMMISSION(this.commission);
        })
        .catch(error => {
          console.log('error:commission', error);
          this.$router.push({ name: 'service.error' });
        })
        .finally(() => {
          this.commissionLoading = false;
        });
    },
    getTildaInfo() {
      const orderId = this.$route.query.orderId;
      const isFromTilda = this.$route.query.action && this.$route.query.action === 'tilda';

      if (!orderId || !isFromTilda) {
        this.loading = false;
        return null;
      }

      let counter = 0;
      this.interval = setInterval(() => {
        this.goTildaRequest(orderId);

        if (counter === 30) {
          clearInterval(this.interval);
          this.loading = false;

          this.$alert({
            type: 'danger',
            title: 'data.is.not.loaded',
          });
        }
        counter++;
      }, 2000);
    },
    goTildaRequest(orderId) {
      return HTTPService.getTildaInfo(orderId, this.project.project_code)
        .then(response => {
          this.readonly = true;

          const { data } = response;

          if (this.$validate.isObjectWithFields(data, ['url']) && this.$validate.isNotEmptyString(data.url)) {
            clearInterval(this.interval);
            window.open(data.url, '_self');
            return;
          }

          // if (this.$validate.isObjectWithFields(data, ['amount']) && this.$validate.isNotNull(data.amount)) {
          //   this.amount = formatAmount(data.amount, {
          //     significant_zeros: this.project.currency.significant_zeros,
          //     type: this.project.currency.type,
          //   });
          // }

          // this.tildaData = data;
          // this.tildaData.techFields = JSON.parse(data.techFields);
          // this.tildaData.customFields = JSON.parse(data.customFields);
          // this.tildaData.products = JSON.parse(data.products);

          clearInterval(this.interval);
          this.loading = false;

          this.SET_TRANSACTION_QUERY(this.$route.query);
        });
    },
    handleLoading(loading) {
      this.loading = loading;
    },
    displayAmountByPaymentDisabled() {
      if (this.project.paymentsEnabled) {
        return;
      }

      this.amount = `XXX ${this.project.currency.sign}`;
    },
    externalCheckoutClear() {
      if (this.hasYooMoneyWidget) {
        this.checkout = null;
        this.$refs.widgetYooMoney.innerHTML = '';
      }
    },
    externalCheckoutInitialize(token, returnUrl) {
      if (this.$validate.isNotNull(this.checkout)) {
        this.externalCheckoutClear();
        this.externalCheckoutInitialize(token, returnUrl);
        return;
      }

      const self = this;

      // Инициализация виджета. Все параметры обязательные.
      this.checkout = new window.YooMoneyCheckoutWidget({
        confirmation_token: token, // Токен, который перед проведением оплаты нужно получить от ЮKassa
        return_url: returnUrl, // Ссылка на страницу завершения оплаты, это может быть любая ваша страница

        // При необходимости можно изменить цвета виджета, подробные настройки см. в документации
        customization: {
          // Выбор способа оплаты для отображения
          payment_methods: this.getPaymentMethods,
        },
        error_callback(error) {
          // Обработка ошибок инициализации
          console.error(error);
          self.externalCheckoutClear();
        },
      });

      // Отображение платежной формы в контейнере
      this.checkout.render('payment-form');
    },
    useSafeDeal(paymentType) {
      if (!this.project.safeDeal) {
        return false;
      }

      return UNSAFE_PAYMENT_TYPES.includes(paymentType);
    },
    handleChangeTipsAmount(amount) {
      this.amount = formatAmount(amount, {
        significant_zeros: this.project.currency.significant_zeros,
        type: this.project.currency.type,
      });
    },
    async validateForm() {
      const emailInput = this.$refs.email;
      const phoneInput = this.$refs.phone;
      const amountInput = this.$refs.amount;

      if (emailInput) {
        emailInput.$v.$touch();

        if (emailInput.$v.$error) {
          emailInput.$refs.email.focus();
        }
      }

      if (phoneInput) {
        phoneInput.$v.$touch();

        if (phoneInput.$v.$error) {
          phoneInput.$refs.phone.focus();
        }
      }

      if (amountInput) {
        amountInput.$v.$touch();

        if (amountInput.$v.$error) {
          amountInput.$refs.price.focus();
        }
      }
    },
    ...mapActions([
      'PROJECT_PAYMENT_CREATE',
    ]),
    ...mapMutations([
      'SET_COMMISSION',
      'SET_TRANSACTION',
      'SET_TRANSACTION_KEY',
      'SET_TRANSACTION_QUERY',
      'SET_APPLE_PAY_SESSION',
      'SET_PAYMENT_TYPE',
    ]),
  },
};
</script>

<style lang="sass" module>
  .header
    width: 100%
    padding: 30px 0

    +respond-to('medium')
      padding: 40px 0
</style>

<style lang="sass" scoped>
  .project-link
    display: block
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap
    padding-top: .3rem

  .payment-amount

    &.single-block
      border-radius: 8px
      overflow: hidden

    button.qiwi-payment-button
      display: flex
      justify-content: center
      align-items: center
      font-weight: 500
      font-size: 14px
      background: #FF8C00
      box-shadow: inset 0px -1px 0px rgba(0, 0, 0, 0.25)

      &.disabled
        background-color: #7b7b7b
        cursor: not-allowed

      &:not(.disabled):hover
        background: #e48109

      img
        max-width: 20px !important
        max-height: 20px !important
        margin-right: 10px

    button.yoomoney-payment-button
      display: flex
      justify-content: center
      align-items: center
      font-weight: 500
      font-size: 14px
      background: #8B3FFD
      box-shadow: inset 0px -1px 0px rgba(0, 0, 0, 0.25)

      &.disabled
        background-color: #7b7b7b
        cursor: not-allowed

      &:not(.disabled):hover
        background: #753dca

      img
        max-width: 20px !important
        max-height: 20px !important
        margin-right: 10px

    button.payme-payment-button
      display: flex
      justify-content: center
      align-items: center
      background: #33CCCC
      box-shadow: inset 0px -1px 0px rgba(0, 0, 0, 0.25)

      &.disabled
        background-color: #7b7b7b
        cursor: not-allowed

      &:not(.disabled):hover
        background: #30b0b0

      img
        max-width: 83px !important
        max-height: 24px !important

    button
      padding: 0
      height: 50px

  .yandex-payment-wrapper
    padding: 0
    background: transparent

  .apple-payment-wrapper
    width: 100%
    height: 50px
    padding: 0
    display: flex
    justify-content: center
    align-items: center
    border-radius: 50px
    background-color: #000

  .google-payment-wrapper
    padding: 0
    background: transparent

  .policy-privacy
    margin-top: 20px
    padding: 0
    font-weight: 400
    font-size: 12px
    line-height: 17px
    color: #212121

    &::v-deep
      a
        display: inline
        color: #9B51E0
        word-break: break-word

  .self-employed-wrapper
    margin-top: 15px

  .payment-buttons
    padding: 0
    margin-top: 20px
    background-color: transparent

    button
      margin: 0

    .payment-button:not(:last-child)
      margin-bottom: 10px
</style>
